import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateUser } from "@/api/mutations/users";
import {
  validateCreateUser,
  type CreateUserInput,
  type ValidationError,
} from "@/api/schemas/user";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMPTY: CreateUserInput = { email: "", password: "", role: "user" };

export function CreateUserDialog({ open, onOpenChange }: Props) {
  const [form, setForm] = useState<CreateUserInput>(EMPTY);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  // #214/#215 (B5-T2): nach Create einmalig das Gitea-Owner-Login zeigen (One-Time).
  const [giteaCreds, setGiteaCreds] = useState<{ username: string; password: string } | null>(null);

  const mutation = useCreateUser();

  function fieldError(f: keyof CreateUserInput) {
    return errors.find((e) => e.field === f)?.message;
  }

  function handleClose(o: boolean) {
    if (!o) {
      setForm(EMPTY);
      setErrors([]);
      setServerError(null);
      setGiteaCreds(null);
    }
    onOpenChange(o);
  }

  function handleSubmit() {
    const v = validateCreateUser(form);
    setErrors(v);
    setServerError(null);
    if (v.length > 0) return;
    mutation.mutate(form, {
      onSuccess: (user) => {
        toast.success(`User ${user.email} erstellt`);
        // Owner-Gitea-Login einmalig zeigen (sonst direkt schließen).
        if (user.giteaPassword && user.giteaUsername) {
          setGiteaCreds({ username: user.giteaUsername, password: user.giteaPassword });
        } else {
          handleClose(false);
        }
      },
      onError: (err) => {
        const msg = (err as Error).message;
        setServerError(msg);
        toast.error(`Create fehlgeschlagen: ${msg}`);
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle>Neuer User</DialogTitle>
          <DialogDescription>
            Email und Password werden zur Anmeldung verwendet. Role bestimmt Permissions.
          </DialogDescription>
        </DialogHeader>

        {giteaCreds ? (
          <div className="space-y-3" data-testid="gitea-owner-creds">
            <p className="text-sm text-text">
              User erstellt. <strong>Gitea-Owner-Login</strong> — jetzt notieren (wird nicht erneut angezeigt):
            </p>
            <div className="space-y-1">
              <Label>Gitea-Username</Label>
              <Input readOnly value={giteaCreds.username} className="font-mono text-xs" data-testid="gitea-owner-username" />
            </div>
            <div className="space-y-1">
              <Label>Gitea-Passwort</Label>
              <Input readOnly value={giteaCreds.password} className="font-mono text-xs" data-testid="gitea-owner-password" />
            </div>
            <p className="text-xs text-subtext0">
              Damit loggt sich der Owner ins Gitea-Web ein und sieht/bearbeitet die Repos seiner Agents.
            </p>
            <DialogFooter>
              <Button onClick={() => handleClose(false)} data-testid="gitea-creds-close">Notiert, schließen</Button>
            </DialogFooter>
          </div>
        ) : (
        <>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="user-email">Email *</Label>
            <Input
              id="user-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="user@firma.ch"
              data-testid="user-field-email"
              autoFocus
            />
            {fieldError("email") && (
              <div className="text-xs text-red" data-testid="user-error-email">
                {fieldError("email")}
              </div>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="user-password">Password *</Label>
            <Input
              id="user-password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="mindestens 8 Zeichen"
              data-testid="user-field-password"
            />
            {fieldError("password") && (
              <div className="text-xs text-red" data-testid="user-error-password">
                {fieldError("password")}
              </div>
            )}
          </div>

          <div className="space-y-1">
            <Label>Role</Label>
            <Select
              value={form.role}
              onValueChange={(v) => setForm({ ...form, role: v as CreateUserInput["role"] })}
            >
              <SelectTrigger data-testid="user-field-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">user (eigene Agents + Sessions)</SelectItem>
                <SelectItem value="admin">admin (volle Plattform-Rechte)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {serverError && (
          <div className="text-sm text-red" data-testid="user-server-error">
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} data-testid="user-cancel">
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={mutation.isPending} data-testid="user-submit">
            {mutation.isPending ? "..." : "User erstellen"}
          </Button>
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
